import { Suspense, useEffect, useRef, useState } from 'react'
import { BASE, api } from './api'
import { ErrorBoundary } from './components/ErrorBoundary'
import { FreshnessBadge } from './components/FreshnessBadge'
import { HomePage } from './components/HomePage'
import { Mark, useFavicons } from './components/Logo'
import { SiteFooter } from './components/SiteFooter'
import { ThemeSwitch } from './components/ThemeSwitch'
import { HeaderSearch } from './components/HeaderSearch'
import { track, loadUmami } from './components/track'
import { DevRibbon, EnvTag, VersionFooter } from './components/Version'
import { AskIcon, CalculatorIcon, CompareIcon, DataIcon, FitIcon, OptimiseIcon, SimulateIcon, TrendingIcon } from './components/icons'
import { AboutPage, NeutralityPage, AdminPage, AssessmentPage, ChatPanel, ComparePage, ContactPage, DataPage, ExplorePage, Calculator, FitPage, GuidedTour, LegalPage, NewsPage, OptimisePage, SimulatePage } from './pages'
import { SITE, DEFAULT_SETTINGS, SettingsContext, VENDOR_LABEL, useSite } from './settings'
import type { UiSettings } from './types'

const TABS = [
  { id: 'calculator', label: 'Calculator', Icon: CalculatorIcon, render: () => null,
    help: 'Monthly cost of a workload on one cloud under its hosting modes side by side \u2014 per token, self-hosted, dedicated cluster \u2014 from your token volume and throughput assumptions, with a break-even.' },
  { id: 'compare', label: 'Compare', Icon: CompareIcon, render: () => <ComparePage />,
    help: 'Pick a model and see what each cloud charges for it: per token, on a dedicated cluster, or self-hosted on a GPU instance the model actually fits on.' },
  { id: 'fit', label: 'Fit', Icon: FitIcon, render: () => <FitPage />,
    help: 'Which hardware can hold a model: its weights plus a KV-cache allowance against each instance\u2019s or cluster shape\u2019s total VRAM, per cloud, with prices. Anything too small is left out.' },
  { id: 'simulations', label: 'Simulate', Icon: SimulateIcon, render: () => null,
    help: 'Several workloads and models priced together over a period, an option chosen for each and one total; kept under a link to share or open again, no account.' },
  { id: 'optimise', label: 'Savings', Icon: OptimiseIcon, render: () => null,
    help: 'How much less a client could pay for the same work: each workload by its use case, a ladder of savings priced from list prices at the client\u2019s own volumes, and the optimised plan beside today.' },
  { id: 'chat', label: 'Ask', Icon: AskIcon, render: () => <ChatPanel />,
    help: 'Ask in plain language; the answer is computed by the same tools the pages use, never quoted from memory.' },
  { id: 'news', label: 'Trending', Icon: TrendingIcon, render: () => <NewsPage />,
    help: 'What changed since the previous snapshot: price cuts and rises, instances and models added or withdrawn, new regions, catalogue additions. Computed from the data, not written by hand.' },
  { id: 'data', label: 'Data', Icon: DataIcon, render: () => <DataPage />,
    help: 'How many prices each cloud contributes, when they were last refreshed, and the exact public sources every number comes from.' },
] as const

/** the page file behind each menu item, fetched when the pointer comes over it, ahead of the click */
const AHEAD: Record<string, () => void> = {
  calculator: Calculator.preload, compare: ComparePage.preload, fit: FitPage.preload, chat: ChatPanel.preload,
  news: NewsPage.preload, data: DataPage.preload, simulations: SimulatePage.preload, optimise: OptimisePage.preload,
}
/** one line under each page's name in the menu's lists */
const SHORT: Record<string, string> = {
  calculator: 'one workload, every cloud and mode, the break-even',
  simulations: 'several workloads over a period, kept under a link',
  optimise: 'where the same work could cost less',
  compare: 'one model on every cloud',
  fit: 'the instances and clusters that hold a model',
}
/** the pages that have a guided demo (the steps themselves are in components/tours.ts, fetched with the demo) */
const DEMOS = ['home', 'calculator', 'compare', 'chat', 'news']

/** What shows for the moment a page's file is on its way. */
function Loading() { return <p className="muted small pageloading" aria-live="polite">loading…</p> }

type TabId = (typeof TABS)[number]['id']
/** The menu: Calculator, Compare, Ask and Trending. Simulate (/simulations) and Savings (/optimise) still open by address
 *  but are off the menu since 2026-10-10: the owner wants both reworked first. Data is reached from the Live pill and the footer. Every page stands alone; none leads into another. */
const MENU: ({ group: string; Icon: typeof CalculatorIcon; items: TabId[] } | { item: TabId })[] = [
  { item: 'calculator' },
  { item: 'compare' },
  { item: 'chat' }, { item: 'news' },
]
/** the landing page is where the site opens; it is not a tab */
type View = TabId | 'home' | 'privacy' | 'terms' | 'about' | 'neutrality' | 'contact' | 'assessment' | 'simulation'
const PAGES: readonly View[] = ['privacy', 'terms', 'about', 'neutrality', 'contact', 'assessment', 'simulation']

/** Every view has an address of its own under the site's home, so it can be linked to, shared and found by a
 *  search engine. The server knows the same list (api/seo.py) and writes each one's title and description. */
const PATHS: Record<View, string> = {
  home: '', calculator: 'calculator', compare: 'compare', fit: 'fit', chat: 'ask', news: 'trending', data: 'data',
  privacy: 'privacy', terms: 'terms', about: 'about', neutrality: 'neutrality', contact: 'contact', assessment: 'assessment', simulations: 'simulations', simulation: 'simulation', optimise: 'optimise',
}
const pathView = (): View | null => {
  const p = window.location.pathname.replace(/\/+$/, '')
  const home = BASE.replace(/\/+$/, '')
  if (!p.startsWith(home)) return null
  const rest = p.slice(home.length).replace(/^\/+/, '')
  return (Object.keys(PATHS) as View[]).find((v) => PATHS[v] === rest) ?? null
}
/** The pages made from the data (a model, a cloud, a GPU, a country, and their lists) live under these; the
 *  rest of the address says which one (components/Explore.tsx). */
const EXPLORE = ['models', 'vendors', 'gpus', 'in-country', 'reports', 'changes']
const explorePath = (): string | null => {
  const p = window.location.pathname.replace(/\/+$/, '')
  const home = BASE.replace(/\/+$/, '')
  if (!p.startsWith(home)) return null
  const rest = p.slice(home.length).replace(/^\/+/, '').toLowerCase()
  return EXPLORE.includes(rest.split('/')[0]) ? rest : null
}
/** a plain click moves within the app; with a modifier key, or the middle button, the browser opens the link */
const plainClick = (e: React.MouseEvent) => !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)

const isAdminPage = () => window.location.pathname.replace(/\/+$/, '').endsWith('/admin')

/** The supplied logo's ring mark, with the site name set in type. The
 *  file's own wordmark reads "analytics estate", so only the mark is used. */
function Brand({ ribbon = true }: { ribbon?: boolean }) {
  const site = useSite()
  return (
    <h1>
      <Mark />
      {site.name}
      {ribbon ? <DevRibbon /> : null}
    </h1>
  )
}

/** Sets the tab and home-screen icons for the site; renders nothing. */
function Favicons() { useFavicons(); return null }

export default function App() {
  // The administrator's settings decide which vendors show, where the
  // calculator starts and which site this is, so everything waits for them.
  const [settings, setSettings] = useState<UiSettings | null>(null)
  const settingsRef = useRef<UiSettings | null>(null)
  useEffect(() => {
    api.uiSettings().then((s) => { settingsRef.current = s; setSettings(s) })
      .catch(() => { settingsRef.current = DEFAULT_SETTINGS; setSettings(DEFAULT_SETTINGS) })
  }, [])
  const [tab, setView] = useState<View>(() => pathView() ?? 'home')
  // the page asked for starts loading now, while the settings are on their way, so it renders without suspending
  useState(() => { AHEAD[tab]?.() })
  // and once the first page is up, the two tools most visitors open next, for a tap that never hovered (a phone)
  useEffect(() => {
    const id = window.setTimeout(() => { AHEAD.calculator(); AHEAD.compare() }, 1500)
    return () => window.clearTimeout(id)
  }, [])
  // a page made from the data, when the address is one: its path under the site's home
  const [explore, setExplore] = useState<string | null>(explorePath)
  // moving between views keeps the address in step: each view has its own
  const setTab = (v: View) => {
    const want = `${BASE}${PATHS[v]}`
    if (window.location.pathname !== want) window.history.pushState(null, '', want)
    setExplore(null)
    setView(v)
    if (PAGES.includes(v)) window.scrollTo(0, 0)
  }
  const goExplore = (path: string) => {
    const want = `${BASE}${path}`
    if (window.location.pathname !== want) window.history.pushState(null, '', want)
    setExplore(path)
  }
  useEffect(() => {
    const back = () => { setExplore(explorePath()); setView(pathView() ?? 'home') }
    window.addEventListener('popstate', back)
    return () => window.removeEventListener('popstate', back)
  }, [])
  // one model for the whole visit: picked on the landing page or on any page,
  // and the Calculator, Compare and Fit all open on it
  const [model, setModel] = useState<string | null>(null)
  // The Ask tab needs a model behind it (ANTHROPIC_API_KEY on the server).
  // Without one it is hidden, not shown as a thing that cannot answer.
  const [chat, setChat] = useState(false)
  useEffect(() => {
    api.health().then((h) => setChat(Boolean(h.chat))).catch(() => setChat(false))
  }, [])
  const tabs = TABS.filter((t) => t.id !== 'chat' || chat)
  const profile = settings?.profile ?? SITE
  const exploring = explore !== null && Boolean(profile.features.explore)
  const home = !exploring && tab === 'home' && profile.features.landing
  const legal = !exploring && (tab === 'privacy' || tab === 'terms') && profile.features.legal
  const company = !exploring && (tab === 'about' || tab === 'neutrality' || tab === 'contact' || tab === 'assessment') && profile.features.footer
  const simulation = !exploring && tab === 'simulation'
  const offTab = home || legal || company || simulation || exploring
  const active = tabs.find((t) => t.id === tab) ?? tabs[0]
  // Fit's address opens Compare, which holds every instance that fits since 2026-10-09
  useEffect(() => { if (tab === 'fit') { window.history.replaceState(null, '', `${BASE}compare`); setView('compare') } }, [tab])
  // a kept simulation's own address is the Simulate page
  const isOn = (id: TabId) => (!offTab && id === active.id) || (simulation && id === 'simulations')
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  // on a phone the header is one row; this opens the menu and the controls under it
  const [navOpen, setNavOpen] = useState(false)
  useEffect(() => { setNavOpen(false) }, [tab])
  useEffect(() => {
    if (!openGroup) return
    const away = (e: MouseEvent) => { if (!(e.target as HTMLElement).closest?.('.navgroup')) setOpenGroup(null) }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenGroup(null) }
    document.addEventListener('mousedown', away); document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc) }
  }, [openGroup])
  // the Ask panel is fetched and mounted the first time it is opened; from then on it stays, hidden behind the
  // other pages, so an answer in progress keeps arriving
  const asking = !offTab && active.id === 'chat'
  const [asked, setAsked] = useState(false)
  useEffect(() => { if (asking) setAsked(true) }, [asking])
  // the guided demo of the current page, when it has one
  const [tour, setTour] = useState<string | null>(null)
  // the page's demo: the landing page has one too; the legal and company pages do not
  const tourKey = home ? 'home' : !offTab && DEMOS.includes(active.id) ? active.id : null
  // count the page being looked at, and load the Umami tracker once it is set up on this server
  const pageKey = exploring ? `explore-${explore!.split('/')[0]}` : home ? 'home' : offTab ? tab : active.id
  // a view whose address is not its key (Ask is /ask, its key chat; Trending /trending, its key news) is sent to
  // Umami at its address, so a funnel or a path names the page a visitor sees
  const pageAt = exploring ? explore! : (PATHS as Record<string, string>)[pageKey] ?? ''
  useEffect(() => { if (settings && !isAdminPage()) track('page_view', pageKey, pageAt !== pageKey ? pageAt : '') }, [pageKey, explore, !!settings])   // eslint-disable-line react-hooks/exhaustive-deps
  // the browser tab and the description follow the view: its own title, after the DEV prefix when there is one
  useEffect(() => {
    if (!settings || exploring) return               // (a data page names itself, from its own answer)
    const meta = isAdminPage() ? undefined : settings.pages?.[pageKey]
    document.title = (document.title.startsWith('DEV · ') ? 'DEV · ' : '') + (meta?.title ?? profile.name)
    if (meta) document.querySelector('meta[name="description"]')?.setAttribute('content', meta.description)
  }, [settings, profile.name, pageKey, exploring])
  useEffect(() => { if (!isAdminPage()) loadUmami(settings?.analytics?.umami_website_id) }, [settings?.analytics?.umami_website_id])
  // /admin is not a tab: it is reached by typing the address, and it is the
  // only place a refresh can be started from a browser, behind a login.
  const isAdmin = window.location.pathname.replace(/\/+$/, '').endsWith('/admin')

  if (isAdmin) {
    return (
      <SettingsContext.Provider value={settings ?? DEFAULT_SETTINGS}>
      <Favicons />
      <div className="app">
        <header className="top">
          <Brand ribbon={false} />
          <span className="sub">administration</span>
          <EnvTag />
          <span className="spacer" />
          <ThemeSwitch />
          <a className="pill" href={`${BASE}how-it-works.html`}>How it works</a>
          <a href={BASE} className="small">← back to the app</a>
        </header>
        <main><ErrorBoundary><Suspense fallback={<Loading />}><AdminPage /></Suspense></ErrorBoundary></main>
        <footer className="muted small" style={{ marginTop: 24 }}><VersionFooter /></footer>
      </div>
      </SettingsContext.Provider>
    )
  }

  if (!settings) return <div className="app"><p className="muted small">loading…</p></div>

  return (
    <SettingsContext.Provider value={settings}>
    <Favicons />
    <div className="app">
      {profile.layout === 'classic' ? (
        <>
          <header className="top">
            <Brand />
            {profile.tagline ? <span className="sub" title={settings.vendors.map((p) => VENDOR_LABEL[p]).join(' · ')}>{profile.tagline}</span> : null}
            <span className="spacer" />
            <ThemeSwitch />
            {tourKey ? (
              <button type="button" className="pill demo" onPointerEnter={GuidedTour.preload} onFocus={GuidedTour.preload}
                onClick={() => { track('demo_start', tourKey); setTour(tourKey) }}>▶ Demo</button>
            ) : null}
            <a className="pill" href={`${BASE}admin`}>Admin</a>
            <FreshnessBadge />
          </header>
          <nav className="tabs" role="tablist">
            {tabs.map((t) => (
              <a key={t.id} role="tab" aria-selected={isOn(t.id)} href={`${BASE}${PATHS[t.id]}`}
                onPointerEnter={AHEAD[t.id]} onFocus={AHEAD[t.id]}
                onClick={(e) => { if (plainClick(e)) { e.preventDefault(); setTab(t.id) } }}>
                <t.Icon />{t.label}
              </a>
            ))}
          </nav>
        </>
      ) : (
      <>
      {/* the name on the left, larger; on the right the controls and, under
          them, the menu; one line under it all */}
      <header className={`top main${navOpen ? ' navopen' : ''}`}>
        <div className="brandblock">
          <button type="button" className="homelink" onClick={() => setTab(profile.features.landing ? 'home' : tabs[0].id)} title="Home" aria-label={`${profile.name} home`}>
            <Mark />
          </button>
          <div>
            <div className="brandname"><button type="button" className="homelink" onClick={() => setTab(profile.features.landing ? 'home' : tabs[0].id)}>{profile.name}</button><DevRibbon /></div>
            {profile.tagline ? <span className="sub" title={settings.vendors.map((p) => VENDOR_LABEL[p]).join(' · ')}>{profile.tagline}</span> : null}
          </div>
        </div>
        <HeaderSearch onModel={(canonical, tool) => { setModel(canonical); setTab(tool) }}
          onCloud={(p) => { if (profile.features.explore && settings.explore?.live.includes('vendors')) goExplore(`vendors/${p}`); else setTab('compare') }}
          onAsk={chat ? (q) => { try { sessionStorage.setItem('ask-prefill', q) } catch { /* no storage */ } setTab('chat') } : undefined} />
        {/* one line: the menu, then the demo, the theme and the live light.
            Admin is not linked: it is reached by typing /admin. */}
        <button type="button" className="menubtn" aria-expanded={navOpen} aria-controls="sitenav" aria-label={navOpen ? 'Close the menu' : 'Open the menu'} onClick={() => setNavOpen((o) => !o)}>
          <svg viewBox="0 0 24 24" aria-hidden="true">{navOpen ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}</svg>
        </button>
        <div className="right" id="sitenav">
          <nav className="tabs" role="tablist">
            {MENU.map((m) => {
              if ('item' in m) {
                const t = tabs.find((x) => x.id === m.item)
                return t ? (
                  <a key={t.id} role="tab" aria-selected={isOn(t.id)} href={`${BASE}${PATHS[t.id]}`}
                    onPointerEnter={AHEAD[t.id]} onFocus={AHEAD[t.id]}
                    onClick={(e) => { if (plainClick(e)) { e.preventDefault(); setTab(t.id) } }}>
                    <t.Icon />{t.label}
                  </a>
                ) : null
              }
              const items = m.items.map((id) => tabs.find((x) => x.id === id)).filter((x): x is (typeof tabs)[number] => Boolean(x))
              const on = items.some((t) => isOn(t.id))
              return (
                <div key={m.group} className={`navgroup${openGroup === m.group ? ' open' : ''}`} onMouseLeave={() => setOpenGroup((g) => (g === m.group ? null : g))}>
                  <button type="button" aria-haspopup="menu" aria-expanded={openGroup === m.group} aria-selected={on}
                    onMouseEnter={() => setOpenGroup(m.group)} onFocus={() => setOpenGroup(m.group)} onClick={() => setOpenGroup(m.group)}>
                    <m.Icon />{m.group}<span className="caret" aria-hidden="true">▾</span>
                  </button>
                  <div className="navmenu" role="menu">
                    {items.map((t) => (
                      <a key={t.id} role="menuitem" aria-current={isOn(t.id) ? 'page' : undefined} href={`${BASE}${PATHS[t.id]}`}
                        onPointerEnter={AHEAD[t.id]} onFocus={AHEAD[t.id]}
                        onClick={(e) => { if (plainClick(e)) { e.preventDefault(); setOpenGroup(null); setTab(t.id) } }}>
                        <t.Icon /><span><b>{t.label}</b><span className="muted small">{SHORT[t.id] ?? ''}</span></span>
                      </a>
                    ))}
                  </div>
                </div>
              )
            })}
          </nav>
          <div className="controls">
            <FreshnessBadge compact onData={() => setTab('data')} />
            {tourKey ? (
              <button type="button" className="round demo" onPointerEnter={GuidedTour.preload} onFocus={GuidedTour.preload}
                aria-label="Demo of this page" title="Demo of this page"
                onClick={() => { track('demo_start', tourKey); setTour(tourKey) }}>
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5v14l12-7z" /></svg>
              </button>
            ) : null}
            <ThemeSwitch round />
          </div>
        </div>
      </header>
      </>
      )}
      {tour ? <Suspense fallback={null}><GuidedTour page={tour} onClose={() => setTour(null)} /></Suspense> : null}

      {settings.maintenance ? (
        <div className="sitenotice" role="status">
          <b>Maintenance mode is on.</b> Visitors get the maintenance page; you see the site because you are signed in.{' '}
          <a href={`${BASE}admin#maintenance`}>Turn it off in Admin</a>
        </div>
      ) : null}
      {settings.notice ? <div className="sitenotice" role="note">{settings.notice}</div> : null}

      {/* The Ask panel stays mounted while other tabs show, so an answer in
          progress keeps streaming and is there when you come back. */}
      <main role="tabpanel">
        <Suspense fallback={<Loading />}>
        {exploring ? (
          <ErrorBoundary key={explore}>
            <ExplorePage path={explore!} go={goExplore} openTool={(tool, canonical) => { setModel(canonical); setTab(tool) }} />
          </ErrorBoundary>
        ) : legal ? (
          <ErrorBoundary key={tab}><LegalPage page={tab} go={setTab} /></ErrorBoundary>
        ) : company ? (
          <ErrorBoundary key={tab}>{tab === 'about' ? <AboutPage go={setTab} chat={chat} /> : tab === 'neutrality' ? <NeutralityPage go={setTab} /> : tab === 'assessment' ? <AssessmentPage go={setTab} /> : <ContactPage go={setTab} />}</ErrorBoundary>
        ) : simulation ? (
          <ErrorBoundary key="simulation"><SimulatePage go={setTab} /></ErrorBoundary>
        ) : home ? (
          <ErrorBoundary key="home">
            <HomePage chat={chat} go={(id) => setTab(id)}
              compare={(canonical) => { setModel(canonical); setTab('compare') }} />
          </ErrorBoundary>
        ) : active.id === 'calculator' ? (
          <ErrorBoundary key="calculator"><Calculator initialModel={model ?? undefined} onModelChange={setModel} onAssessment={() => setTab('assessment')} /></ErrorBoundary>
        ) : active.id === 'compare' ? (
          <ErrorBoundary key="compare"><ComparePage initialModel={model ?? undefined} onModelChange={setModel} onAssessment={() => setTab('assessment')} /></ErrorBoundary>
        ) : active.id === 'simulations' ? (
          <ErrorBoundary key="simulations"><SimulatePage list go={setTab} /></ErrorBoundary>
        ) : active.id === 'optimise' ? (
          <ErrorBoundary key="optimise"><OptimisePage go={setTab} /></ErrorBoundary>
        ) : active.id === 'fit' ? (
          <ErrorBoundary key="fit"><FitPage initialModel={model ?? undefined} onModelChange={setModel} onAssessment={() => setTab('assessment')} /></ErrorBoundary>
        ) : active.id !== 'chat' ? <ErrorBoundary key={active.id}>{active.render()}</ErrorBoundary> : null}
        {asked && tabs.some((t) => t.id === 'chat') ? (
          <div style={{ display: asking ? 'contents' : 'none' }}><ErrorBoundary key="chat"><ChatPanel onAssessment={() => setTab('assessment')} /></ErrorBoundary></div>
        ) : null}
        </Suspense>
      </main>

      {profile.features.footer ? <SiteFooter go={setTab} chat={chat} explore={profile.features.explore ? goExplore : undefined} /> : (
        <footer className="muted small" style={{ marginTop: 24 }} data-tour="version"><VersionFooter /></footer>
      )}
    </div>
    </SettingsContext.Provider>
  )
}
